/**
 * InComm card / purse / reward reporting API — read-only admin reports.
 */

import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

export interface CardStatusCountItem {
  status: string | null;
  count: number;
}

export interface IncommCardsSummaryResponse {
  card_status_counts: CardStatusCountItem[];
  total_cardholders: number;
  total_purses: number;
  total_purse_balance: string;
  total_reward_rows: number;
  total_reward_amount: string;
}

export interface PartnerCardBreakdownItem {
  partner_product_id: number | null;
  partner_name: string | null;
  doing_business_as: string | null;
  counts: CardStatusCountItem[];
  total_count: number;
}

export interface PartnerCardBreakdownResponse {
  items: PartnerCardBreakdownItem[];
}

export interface IncommCardListItem {
  eligible_participant_id: number | string;
  health_plan_code: string;
  program_code: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  card_status: string | null;
  card_last4: string | null;
  wallet_balance: string | null;
  shipped_date: string | null;
  created_at: string;
  partner_product_id: number | null;
  partner_name: string | null;
  doing_business_as: string | null;
}

export interface IncommCardListResponse {
  items: IncommCardListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface IncommCardListParams {
  page: number;
  page_size: number;
  /** Lore EPID (digits) or InComm-facing external_member_id (exact match). */
  member_id?: string;
  card_status?: string;
  /** When true, API returns only rows with NULL card_status (not yet reflected from InComm). */
  card_status_is_null?: boolean;
  partner_product_id?: number;
  search?: string;
}

export interface PurseDetailItem {
  eligible_participant_id: number | string;
  purse_name: string;
  health_plan_code: string;
  program_code: string;
  created_at: string;
  effective_date: string | null;
  expiry_date: string | null;
  incomm_wallet_id: string | null;
  purse_balance: string | null;
  incomm_processed_status: string | null;
  incomm_processed_at: string | null;
  sent_to_incomm_at: string | null;
  sent_to_incomm_filename: string | null;
  updated_at: string | null;
}

export interface RewardDetailItem {
  id: number;
  fulfillment_id: string;
  eligible_participant_id: number | string;
  created_at: string;
  reward_amount: string | null;
  sent_to_incomm_at: string | null;
  sent_to_incomm_filename: string | null;
  incomm_processed_status: string | null;
  incomm_response_message: string | null;
  incomm_response_file: string | null;
  incomm_processed_at: string | null;
  /**
   * Deterministic HMAC-SHA256 pseudonym of `fulfillment_id` (base32, 29 chars).
   * What the backend sends to InComm as the Purse Reward `Reference Number` when
   * `INCOMM_USE_PSEUDONYMS=true`. Computed server-side per row; no extra DB lookup.
   */
  external_reference: string;
}

export interface LiveCardDetail {
  serial_number: number;
  card_type: string | null;
  card_display_number: string | null;
  issue_date_utc: string | null;
  expiration_date_utc: string | null;
  scan_to_pay_code: string | null;
  shipment_tracking_number: string | null;
  shipment_delivery_method: string | null;
}

export interface LiveWalletItem {
  wallet_id: number;
  display_name: string | null;
  name: string | null;
  balance: string;
  status: string | null;
  start_date_utc: string | null;
  end_date_utc: string | null;
  benefit_type: string | null;
  wallet_type: string | null;
}

export interface IncommCardDetailResponse {
  eligible_participant_id: number | string;
  health_plan_code: string;
  program_code: string;
  package_id: string;
  created_at: string;
  member_no_2: string | null;
  /**
   * InComm-facing Member ID (forward-only contract). Legacy rows hold the raw
   * `eligible_participant_id` as a string with `pseudonym_key_version=0`; new
   * rows hold a 20-char base32 HMAC pseudonym with `pseudonym_key_version>=1`.
   */
  external_member_id: string;
  /** InComm-facing Member ID 2 (pseudonym of `ref_user_id`), if populated. */
  external_member_id_2: string | null;
  /** InComm-facing Group ID (pseudonym of `partner_product_id`), if populated. */
  external_group_id: string | null;
  /**
   * HMAC key version used to compute the three `external_*` columns above.
   * `0` = raw legacy value; `>= 1` = base32 HMAC pseudonym.
   */
  pseudonym_key_version: number;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  date_of_birth: string | null;
  group_number: string | null;
  udf_1: string | null;
  udf_2: string | null;
  initial_activation_code: string | null;
  e_card: boolean | null;
  mbi: string | null;
  incomm_processed_status: string | null;
  incomm_processed_at: string | null;
  incomm_return_info: Record<string, unknown> | null;
  updated_at: string | null;
  sent_to_incomm_at: string | null;
  sent_to_incomm_filename: string | null;
  card_status: string | null;
  card_last4: string | null;
  wallet_balance: string | null;
  shipped_date: string | null;
  last_profile_enriched_at: string | null;
  partner_product_id: number | null;
  partner_name: string | null;
  doing_business_as: string | null;
  live_profile_status: 'ok' | 'unavailable';
  enrollment_status: string | null;
  enrollment_status_id: number | null;
  enrollment_start_utc: string | null;
  enrollment_end_utc: string | null;
  health_plan_name: string | null;
  program_name: string | null;
  live_card: LiveCardDetail | null;
  live_wallets: LiveWalletItem[];
  purses: PurseDetailItem[];
  rewards: RewardDetailItem[];
}

export async function fetchIncommCardsSummary(): Promise<IncommCardsSummaryResponse> {
  const response = await api.get<IncommCardsSummaryResponse>(API_ENDPOINTS.admin.incommCardsSummary);
  return response.data;
}

export async function fetchIncommCardsPartnerBreakdown(): Promise<PartnerCardBreakdownResponse> {
  const response = await api.get<PartnerCardBreakdownResponse>(
    API_ENDPOINTS.admin.incommCardsSummaryByPartner
  );
  return response.data;
}

export async function fetchIncommCardList(
  params: IncommCardListParams
): Promise<IncommCardListResponse> {
  const response = await api.get<IncommCardListResponse>(API_ENDPOINTS.admin.incommCards, {
    params: {
      ...params,
      card_status_is_null: params.card_status_is_null === true ? true : undefined,
    },
  });
  return response.data;
}

export async function fetchIncommCardDetail(
  eligibleParticipantId: number | string
): Promise<IncommCardDetailResponse> {
  const response = await api.get<IncommCardDetailResponse>(
    API_ENDPOINTS.admin.incommCardDetail(eligibleParticipantId)
  );
  return response.data;
}
